import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LockKeyhole, ShieldCheck } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, getErrorMessage } from "@/lib/api";
import { getDefaultLandingPath } from "@/lib/portalModules";
import { captureFrontendException } from "@/lib/monitoring";
import { BrandLockup } from "@/components/brand/BrandLockup";

const initialBootstrapState = {
  loading: true,
  setup_required: false,
  bootstrap_allowed: false,
  has_users: false,
};

export const LoginPage = ({ onBootstrapComplete, onLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({
    email: "",
    password: "",
    remember_me: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [bootstrapStatus, setBootstrapStatus] = useState(initialBootstrapState);
  const [setupForm, setSetupForm] = useState({
    full_name: "",
    email: "",
    password: "",
    token: "",
  });
  const [setupSubmitting, setSetupSubmitting] = useState(false);
  const [setupError, setSetupError] = useState("");
  const environmentLabel = useMemo(() => {
    if (typeof window === "undefined") return "";
    const host = window.location.hostname;
    if (host.includes("preview") || host.includes("staging")) return "Preview";
    if (host.includes("localhost")) return "Local";
    return "";
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadBootstrapStatus = async () => {
      try {
        const response = await api.get("/auth/bootstrap-status");
        if (mounted) {
          setBootstrapStatus({ loading: false, ...response.data });
        }
      } catch (requestError) {
        captureFrontendException(requestError, { source: "bootstrap-status" });
        if (mounted) {
          setBootstrapStatus({ ...initialBootstrapState, loading: false });
        }
      }
    };

    loadBootstrapStatus();

    return () => {
      mounted = false;
    };
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const user = await onLogin(form);
      const from = location.state?.from || getDefaultLandingPath(user?.role) || "/overview";
      navigate(from, { replace: true });
    } catch (requestError) {
      setError(getErrorMessage(requestError, "Unable to access the Team Portal right now."));
    } finally {
      setSubmitting(false);
    }
  };

  const submitBootstrap = async (event) => {
    event.preventDefault();
    setSetupSubmitting(true);
    setSetupError("");
    try {
      await api.post("/auth/bootstrap-admin", setupForm);
      await onBootstrapComplete();
      navigate("/overview", { replace: true });
    } catch (requestError) {
      setSetupError(getErrorMessage(requestError, "Unable to create the initial administrator."));
    } finally {
      setSetupSubmitting(false);
    }
  };

  return (
    <div className="login-layout">
      <section className="login-hero-panel">
        <div className="login-hero-overlay" />
        <div className="login-hero-content">
          <BrandLockup className="login-brand-lockup" descriptor="TEAMS" testId="login-hero-brand-lockup" variant="dark" />

          <div className="login-hero-copy">
            <p className="eyebrow">Internal operating engine</p>
            <h1>VenuLoQ Teams</h1>
            <p>Internal operating platform for the people running venues, vendors, relationships, finance, and execution.</p>
          </div>
        </div>
      </section>

      <section className="login-panel">
        <div className="login-card-shell">
          {environmentLabel ? (
            <div className="login-environment-chip" data-testid="login-environment-chip">
              {environmentLabel}
            </div>
          ) : null}
          <div className="login-brand-row">
            <BrandLockup className="login-panel-brand-lockup" descriptor="TEAMS" testId="login-panel-brand-lockup" variant="light" />
            <p className="login-trust-line">Internal operating platform</p>
          </div>

          <div className="login-header-block">
            <h2>Welcome to VenuLoQ Teams</h2>
            <p>Sign in to access your internal workspace for operations, acquisition, RM, vendors, finance, and execution.</p>
          </div>

          {error ? (
            <Alert className="error-alert" data-testid="login-error-alert">
              <AlertTitle>Access blocked</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <form className="login-form" onSubmit={submit}>
            <label className="field-stack">
              <span>Email address</span>
              <Input
                data-testid="login-form-email-input"
                placeholder="name@venuloq.com"
                type="email"
                value={form.email}
                onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                required
              />
            </label>

            <label className="field-stack">
              <span>Password</span>
              <Input
                data-testid="login-form-password-input"
                placeholder="Enter your password"
                type="password"
                value={form.password}
                onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
                required
              />
            </label>

            <div className="remember-row">
              <div className="remember-copy">
                <strong>Remember me</strong>
                <p>Keep me signed in on this device.</p>
              </div>
              <Switch
                checked={form.remember_me}
                data-testid="login-form-remember-switch"
                onCheckedChange={(checked) => setForm((current) => ({ ...current, remember_me: checked }))}
              />
            </div>

            <Button className="gold-button" data-testid="login-form-submit-button" disabled={submitting} type="submit">
              {submitting ? "Securing workspace…" : "Access Platform"}
            </Button>
          </form>

          <div className="inline-note login-security-note">
            <ShieldCheck size={14} />
            <span>Staff-only access. Use your issued VenuLoQ credentials.</span>
          </div>

          {!bootstrapStatus.loading && bootstrapStatus.setup_required ? (
            <Card className="surface-card light-card">
              <CardHeader>
                <CardTitle>Initial administrator setup</CardTitle>
                <CardDescription>
                  Use this one-time setup only when no active admin exists and secure bootstrap is enabled via environment flags.
                </CardDescription>
              </CardHeader>
              <CardContent className="page-stack">
                {bootstrapStatus.bootstrap_allowed ? null : (
                  <Alert className="error-alert" data-testid="bootstrap-disabled-alert">
                    <AlertTitle>Bootstrap currently disabled</AlertTitle>
                    <AlertDescription>
                      Set `ALLOW_ADMIN_BOOTSTRAP=true` and `ADMIN_BOOTSTRAP_TOKEN` in the backend environment to enable initial admin setup.
                    </AlertDescription>
                  </Alert>
                )}

                {setupError ? (
                  <Alert className="error-alert" data-testid="bootstrap-error-alert">
                    <AlertTitle>Admin setup failed</AlertTitle>
                    <AlertDescription>{setupError}</AlertDescription>
                  </Alert>
                ) : null}

                <form className="login-form" onSubmit={submitBootstrap}>
                  <label className="field-stack">
                    <span>Full name</span>
                    <Input data-testid="bootstrap-admin-full-name-input" value={setupForm.full_name} onChange={(event) => setSetupForm((current) => ({ ...current, full_name: event.target.value }))} required />
                  </label>
                  <label className="field-stack">
                    <span>Admin email</span>
                    <Input data-testid="bootstrap-admin-email-input" type="email" value={setupForm.email} onChange={(event) => setSetupForm((current) => ({ ...current, email: event.target.value }))} required />
                  </label>
                  <label className="field-stack">
                    <span>Admin password</span>
                    <Input data-testid="bootstrap-admin-password-input" type="password" value={setupForm.password} onChange={(event) => setSetupForm((current) => ({ ...current, password: event.target.value }))} required />
                  </label>
                  <label className="field-stack">
                    <span>Bootstrap token</span>
                    <Input data-testid="bootstrap-admin-token-input" type="password" value={setupForm.token} onChange={(event) => setSetupForm((current) => ({ ...current, token: event.target.value }))} required />
                  </label>
                  <Button className="gold-button" data-testid="bootstrap-admin-submit-button" disabled={setupSubmitting || !bootstrapStatus.bootstrap_allowed} type="submit">
                    {setupSubmitting ? "Creating administrator…" : "Create initial administrator"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </section>
    </div>
  );
};
